import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  FileEdit, Clock, CheckCircle2, XCircle, Loader2, Mail,
  Building2, User, ArrowRight, Send, ChevronRight, Calendar
} from "lucide-react";

export default function AdminAccountRequests() {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, []);

  const fetchRequests = async (status = null) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const url = status ? `${API}/admin/account-requests?status=${status}` : `${API}/admin/account-requests`;
      const response = await axios.get(url, { headers });
      setRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch requests:", error);
      toast.error("Failed to load account requests");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/account-requests/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    if (value === "all") {
      fetchRequests();
    } else {
      fetchRequests(value);
    }
  };

  const handleReview = async (status) => {
    if (!selectedRequest) return;
    
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/account-requests/${selectedRequest.id}/review`, {
        status,
        admin_notes: adminNotes || null
      }, { headers });
      
      toast.success(`Request ${status.toLowerCase()}`);
      setReviewDialogOpen(false);
      setSelectedRequest(null);
      setAdminNotes("");
      fetchRequests(activeTab === "all" ? null : activeTab);
      fetchStats();
    } catch (error) {
      console.error("Failed to review request:", error);
      toast.error(error.response?.data?.detail || "Failed to process review");
    } finally {
      setSubmitting(false);
    }
  };

  const openReviewDialog = (request) => {
    setSelectedRequest(request);
    setAdminNotes("");
    setReviewDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "PENDING":
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "APPROVED":
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Approved</Badge>;
      case "REJECTED":
        return <Badge className="bg-red-500/10 text-red-500 border-red-500/30"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredRequests = requests.filter(r => {
    if (activeTab === "all") return true;
    return r.status === activeTab.toUpperCase();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Account Requests</h1>
          <p className="text-muted-foreground mt-1">
            Review and approve profile change requests from businesses
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Requests</p>
              </div>
              <FileEdit className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-yellow-500/5 border-yellow-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
                <p className="text-sm text-muted-foreground">Pending Review</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/5 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-500">{stats.approved}</p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-500/5 border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-500">{stats.rejected}</p>
                <p className="text-sm text-muted-foreground">Rejected</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests List */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" className="relative">
            Pending
            {stats.pending > 0 && (
              <Badge className="ml-2 h-5 px-1.5 bg-yellow-500 text-white">{stats.pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              {filteredRequests.length === 0 ? (
                <div className="p-12 text-center">
                  <FileEdit className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No {activeTab} requests</h3>
                  <p className="text-muted-foreground">
                    {activeTab === "pending" 
                      ? "All caught up! No pending requests to review." 
                      : `No ${activeTab} requests found.`}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredRequests.map((request) => (
                    <div 
                      key={request.id} 
                      className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => openReviewDialog(request)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Building2 className="h-5 w-5 text-primary" />
                            <h3 className="font-semibold">{request.business_name}</h3>
                            {getStatusBadge(request.status)}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {request.requested_by_email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(request.created_at)}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {Object.keys(request.changes).map(field => (
                              <Badge key={field} variant="outline" className="text-xs">
                                {field.replace(/_/g, " ")}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="h-5 w-5" />
              Account Change Request
            </DialogTitle>
            <DialogDescription>
              Review the requested changes for {selectedRequest?.business_name}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6">
              {/* Request Info */}
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Business:</span>
                    <p className="font-medium">{selectedRequest.business_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Requested by:</span>
                    <p className="font-medium">{selectedRequest.requested_by_email}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Submitted:</span>
                    <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                  </div>
                </div>
                {selectedRequest.reason && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <span className="text-muted-foreground text-sm">Reason for changes:</span>
                    <p className="mt-1">{selectedRequest.reason}</p>
                  </div>
                )}
              </div>

              {/* Changes */}
              <div>
                <h4 className="font-semibold mb-3">Requested Changes</h4>
                <div className="space-y-3">
                  {Object.entries(selectedRequest.changes).map(([field, newValue]) => {
                    const currentValue = selectedRequest.current_values?.[field];
                    return (
                      <div key={field} className="p-3 rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                          {field.replace(/_/g, " ")}
                        </p>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 p-2 rounded bg-red-500/5 border border-red-500/20">
                            <p className="text-sm text-red-400">
                              {typeof currentValue === 'object' 
                                ? JSON.stringify(currentValue, null, 2) 
                                : currentValue || 'Not set'}
                            </p>
                          </div>
                          <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 p-2 rounded bg-green-500/5 border border-green-500/20">
                            <p className="text-sm text-green-400">
                              {typeof newValue === 'object' 
                                ? JSON.stringify(newValue, null, 2) 
                                : newValue || 'Not set'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Admin Notes & Actions */}
              {selectedRequest.status === "PENDING" && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Admin Notes (optional)</label>
                    <Textarea 
                      spellCheck="true"
                      placeholder="Add notes about this decision..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                      className="mt-2"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleReview("APPROVED")}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                      )}
                      Approve Changes
                    </Button>
                    <Button 
                      variant="destructive"
                      className="flex-1"
                      onClick={() => handleReview("REJECTED")}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <XCircle className="mr-2 h-4 w-4" />
                      )}
                      Reject
                    </Button>
                  </div>
                </div>
              )}

              {/* Already reviewed info */}
              {selectedRequest.status !== "PENDING" && (
                <div className="p-4 rounded-lg bg-muted/30 border border-border">
                  <p className="text-sm text-muted-foreground">
                    <strong>Reviewed by:</strong> {selectedRequest.reviewed_by || "Unknown"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Reviewed at:</strong> {formatDate(selectedRequest.reviewed_at)}
                  </p>
                  {selectedRequest.admin_notes && (
                    <p className="text-sm mt-2">
                      <strong>Notes:</strong> {selectedRequest.admin_notes}
                    </p>
                  )}
                </div>
              )}

              {/* Email Contact Button */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => window.open(`mailto:${selectedRequest.requested_by_email}?subject=Re: Account Change Request - ${selectedRequest.business_name}`)}
              >
                <Mail className="mr-2 h-4 w-4" />
                Contact {selectedRequest.requested_by_email}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
